import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider, Outlet, useParams, Navigate } from 'react-router-dom'
import { RequireAuth } from '@/components/layout/RequireAuth'
import { RequirePlatformAdmin } from '@/components/layout/RequirePlatformAdmin'
import { RequireTenantAdmin } from '@/components/layout/RequireTenantAdmin'
import { RequireFeature } from '@/components/RequireFeature'
import { TenantProvider } from '@/contexts/TenantProvider'
import ErrorBoundary from '@/components/ui/ErrorBoundary'

/* ============================================================
   Lazy-loaded Pages (Code Splitting per PRD §7)
   ============================================================ */

// Public
const LandingPage = lazy(() => import('@/pages/public/LandingPage'))
const OriginLanding = lazy(() => import('@/pages/public/OriginLanding'))
const AssetPreview = lazy(() => import('@/pages/public/AssetPreview'))
const ParagonLanding = lazy(() => import('@/pages/public/ParagonLanding'))
const PricingPage = lazy(() => import('@/pages/public/PricingPage'))
const LoginPage = lazy(() => import('@/pages/public/LoginPage'))
// Signups disabled — routes redirect to login (see route config below)
// const SignupPage = lazy(() => import('@/pages/public/SignupPage'))
const ScrollEffectsDemo = lazy(() => import('@/pages/public/ScrollEffectsDemo'))
const ComponentDemo = lazy(() => import('@/pages/demo/ComponentDemo'))
const ForgotPasswordPage = lazy(() => import('@/pages/public/ForgotPasswordPage'))
const TermsPage = lazy(() => import('@/pages/public/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/public/PrivacyPage'))

// Tenant Public (consumer-facing)
const TenantSite = lazy(() => import('@/pages/tenant/TenantSite'))
const QuizPage = lazy(() => import('@/pages/tenant/QuizPage'))
const BookingPage = lazy(() => import('@/pages/tenant/BookingPage'))
const FlashOpeningsPage = lazy(() => import('@/pages/tenant/FlashOpeningsPage'))
const LooksPage = lazy(() => import('@/pages/tenant/LooksPage'))
const VisualSearch = lazy(() => import('@/pages/public/VisualSearch'))

// Tenant Admin
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout'))
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'))
const AdminDashboardLiquidPreview = lazy(() => import('@/pages/admin/AdminDashboardLiquidPreview'))
const AdminContent = lazy(() => import('@/pages/admin/AdminContent'))
const AdminTheme = lazy(() => import('@/pages/admin/AdminTheme'))
const AdminStylists = lazy(() => import('@/pages/admin/AdminStylists'))
const AdminServices = lazy(() => import('@/pages/admin/AdminServices'))
const AdminRecipes = lazy(() => import('@/pages/admin/AdminRecipes'))
const AdminBookings = lazy(() => import('@/pages/admin/AdminBookings'))
const AdminEvents = lazy(() => import('@/pages/admin/AdminEvents'))
const AdminTestimonials = lazy(() => import('@/pages/admin/AdminTestimonials'))
const AdminGallery = lazy(() => import('@/pages/admin/AdminGallery'))
const AdminFAQ = lazy(() => import('@/pages/admin/AdminFAQ'))
const AdminCards = lazy(() => import('@/pages/admin/AdminCards'))
const AdminClients = lazy(() => import('@/pages/admin/AdminClients'))
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'))
const AdminAnalytics = lazy(() => import('@/pages/admin/AdminAnalytics'))
const AdminActivity = lazy(() => import('@/pages/admin/AdminActivity'))
const OnboardingWizard = lazy(() => import('@/pages/admin/OnboardingWizard'))
const AdminSEO = lazy(() => import('@/pages/admin/AdminSEO'))
const AdminIntegrations = lazy(() => import('@/pages/admin/AdminIntegrations'))
const AdminCmsEditor = lazy(() => import('@/pages/admin/AdminCmsEditor'))
const AdminDomains = lazy(() => import('@/pages/admin/AdminDomains'))
const AdminSchedule = lazy(() => import('@/pages/admin/AdminSchedule'))
const AdminBusinessHours = lazy(() => import('@/pages/admin/AdminBusinessHours'))
const MarketingAutomation = lazy(() => import('@/pages/admin/MarketingAutomation'))
const TemplateGallery = lazy(() => import('@/pages/admin/TemplateGallery'))
const AdminMessaging = lazy(() => import('@/pages/admin/AdminMessaging'))
const AdminWorkflows = lazy(() => import('@/pages/admin/AdminWorkflows'))
const Admin3DBackground = lazy(() => import('@/pages/admin/Admin3DBackground'))
const AdminClientDNA = lazy(() => import('@/pages/admin/AdminClientDNA'))
const AdminRetailOrders = lazy(() => import('@/pages/admin/AdminRetailOrders'))
const AdminInventory = lazy(() => import('@/pages/admin/AdminInventory'))
const AdminInstagram = lazy(() => import('@/pages/admin/AdminInstagram'))
const AdminNotifications = lazy(() => import('@/pages/admin/AdminNotifications'))
const AdminFlashOpenings = lazy(() => import('@/pages/admin/AdminFlashOpenings'))
const AdminReviews = lazy(() => import('@/pages/admin/AdminReviews'))
const AdminAIDefense = lazy(() => import('@/pages/admin/AdminAIDefense'))
const AdminBookingWidget = lazy(() => import('@/pages/admin/AdminBookingWidget'))
const TemplateMarketplace = lazy(() => import('@/pages/admin/TemplateMarketplace'))
const RetailCheckout = lazy(() => import('@/pages/tenant/RetailCheckout'))
const ClientPortal = lazy(() => import('@/pages/tenant/ClientPortal'))
const ClientLoginPage = lazy(() => import('@/pages/tenant/ClientLoginPage'))
// const ClientSignupPage = lazy(() => import('@/pages/tenant/ClientSignupPage'))

// Platform Admin
const PlatformLayout = lazy(() => import('@/components/layout/PlatformLayout'))
const PlatformDashboard = lazy(() => import('@/pages/platform/PlatformDashboard'))
const PlatformTenants = lazy(() => import('@/pages/platform/PlatformTenants'))
const PlatformBilling = lazy(() => import('@/pages/platform/PlatformBilling'))
const PlatformAnalytics = lazy(() => import('@/pages/platform/PlatformAnalytics'))
const PlatformSettings = lazy(() => import('@/pages/platform/PlatformSettings'))
const PlatformAuditLogs = lazy(() => import('@/pages/platform/PlatformAuditLogs'))
const PlatformLeaderboard = lazy(() => import('@/pages/platform/PlatformLeaderboard'))
const PlatformTenantDetail = lazy(() => import('@/pages/platform/PlatformTenantDetail'))
const PlatformHealth = lazy(() => import('@/pages/platform/PlatformHealth'))
const PlatformLoginPage = lazy(() => import('@/pages/platform/PlatformLoginPage'))

// PARAGON Agency Admin
const RequireAgencyAdmin = lazy(() => import('@/components/layout/RequireAgencyAdmin'))
const ParagonLayout = lazy(() => import('@/components/paragon/ParagonLayout'))
const ParagonFleetHealth = lazy(() => import('@/pages/paragon/ParagonFleetHealth'))
const ParagonMigrationDLQ = lazy(() => import('@/pages/paragon/ParagonMigrationDLQ'))
const ParagonLibraryPanel = lazy(() => import('@/pages/paragon/ParagonLibraryPanel'))
const ParagonImpersonation = lazy(() => import('@/pages/paragon/ParagonImpersonation'))
const ParagonLoginPage = lazy(() => import('@/pages/paragon/ParagonLoginPage'))

// Not Found
const NotFound = lazy(() => import('@/pages/public/NotFound'))

/* ── Route Wrappers (extract params for components that need them) ── */
function AdminClientDNARoute() {
  const { clientId = '' } = useParams<{ clientId: string }>()
  return <AdminClientDNA clientId={clientId} />
}

/** Signups disabled — redirect /:slug/signup → /:slug/login */
function TenantSignupRedirect() {
  const { slug = '' } = useParams<{ slug: string }>()
  return <Navigate to={`/${slug}/login`} replace />
}

/* ============================================================
   Loading Fallback
   ============================================================ */

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-bg-primary">
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-2 border-white/10 border-t-origin-accent animate-spin" />
        </div>
        <p className="text-text-muted text-sm font-mono tracking-wider">LOADING</p>
      </div>
    </div>
  )
}

function SuspenseLayout() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  )
}

/* ============================================================
   Router Configuration (PRD §7)
   ============================================================ */

// eslint-disable-next-line react-refresh/only-export-components
export const router = createBrowserRouter(
  [
    {
      element: <SuspenseLayout />,
      children: [
        /* ── Public Routes ── */
        { path: '/', element: <LandingPage /> },
        { path: '/phyre', element: <LandingPage /> },
        { path: '/origin', element: <OriginLanding /> },
        { path: '/origin/assets', element: <AssetPreview /> },
        { path: '/paragon', element: <ParagonLanding /> },
        { path: '/pricing', element: <PricingPage /> },
        { path: '/origin/login', element: <LoginPage /> },
        { path: '/origin/signup', element: <Navigate to="/origin/login" replace /> },
        { path: '/login', element: <LoginPage /> },
        { path: '/signup', element: <Navigate to="/login" replace /> },
        { path: '/scroll-demo', element: <ScrollEffectsDemo /> },
        { path: '/demo/components', element: <ComponentDemo /> },
        { path: '/forgot-password', element: <ForgotPasswordPage /> },
        { path: '/terms', element: <TermsPage /> },
        { path: '/privacy', element: <PrivacyPage /> },

        /* ── Platform Login (public) ── */
        { path: '/platform/login', element: <PlatformLoginPage /> },

        /* ── Platform Admin (requires platform_admin role) ── */
        {
          path: '/platform',
          element: (
            <RequireAuth>
              <RequirePlatformAdmin>
                <PlatformLayout />
              </RequirePlatformAdmin>
            </RequireAuth>
          ),
          children: [
            { index: true, element: <PlatformDashboard /> },
            { path: 'tenants', element: <PlatformTenants /> },
            { path: 'tenants/:id', element: <PlatformTenantDetail /> },
            { path: 'billing', element: <PlatformBilling /> },
            { path: 'analytics', element: <PlatformAnalytics /> },
            { path: 'leaderboard', element: <PlatformLeaderboard /> },
            { path: 'settings', element: <PlatformSettings /> },
            { path: 'audit-logs', element: <PlatformAuditLogs /> },
            { path: 'health', element: <PlatformHealth /> },
          ],
        },

        /* ── Tenant Admin (requires tenant admin role) ── */
        {
          path: '/:slug/admin',
          element: (
            <RequireAuth>
              <RequireTenantAdmin>
                <TenantProvider>
                  <AdminLayout />
                </TenantProvider>
              </RequireTenantAdmin>
            </RequireAuth>
          ),
          children: [
            { index: true, element: <AdminDashboard /> },
            { path: 'liquid-preview', element: <AdminDashboardLiquidPreview /> },
            { path: 'content', element: <AdminContent /> },
            { path: 'theme', element: <AdminTheme /> },
            { path: 'stylists', element: <AdminStylists /> },
            { path: 'services', element: <AdminServices /> },
            { path: 'recipes', element: <AdminRecipes /> },
            { path: 'bookings', element: <AdminBookings /> },
            { path: 'events', element: <AdminEvents /> },
            { path: 'testimonials', element: <AdminTestimonials /> },
            { path: 'gallery', element: <AdminGallery /> },
            { path: 'faq', element: <AdminFAQ /> },
            { path: 'cards', element: <AdminCards /> },
            { path: 'clients', element: <AdminClients /> },
            { path: 'settings', element: <AdminSettings /> },
            { path: 'analytics', element: <AdminAnalytics /> },
            { path: 'activity', element: <AdminActivity /> },
            { path: 'seo', element: <AdminSEO /> },
            { path: 'integrations', element: <AdminIntegrations /> },
            { path: 'onboarding', element: <OnboardingWizard /> },
            { path: 'domains', element: <AdminDomains /> },
            { path: 'schedule', element: <AdminSchedule /> },
            { path: 'business-hours', element: <AdminBusinessHours /> },
            { path: 'marketing', element: <MarketingAutomation /> },
            { path: 'templates', element: <TemplateGallery /> },
            { path: 'messaging', element: <AdminMessaging /> },
            { path: 'workflows', element: <AdminWorkflows /> },
            { path: '3d-background', element: <Admin3DBackground /> },
            { path: 'client-dna', element: <AdminClientDNARoute /> },
            { path: 'client-dna/:clientId', element: <AdminClientDNARoute /> },
            { path: 'retail-orders', element: <AdminRetailOrders /> },
            { path: 'inventory', element: <AdminInventory /> },
            { path: 'instagram', element: <AdminInstagram /> },
            { path: 'notifications', element: <AdminNotifications /> },
            { path: 'flash-openings', element: <AdminFlashOpenings /> },
            { path: 'reviews', element: <AdminReviews /> },
            { path: 'marketplace', element: <TemplateMarketplace /> },
            { path: 'ai-defense', element: <AdminAIDefense /> },
            { path: 'booking-widget', element: <AdminBookingWidget /> },
          ],
        },

        /* ── CMS Editor (full-screen, outside AdminLayout) ── */
        {
          path: '/:slug/admin/editor',
          element: (
            <RequireAuth>
              <RequireTenantAdmin>
                <TenantProvider>
                  <RequireFeature feature="cmsEditor">
                    <AdminCmsEditor />
                  </RequireFeature>
                </TenantProvider>
              </RequireTenantAdmin>
            </RequireAuth>
          ),
        },

        /* ── Tenant Public (consumer-facing, wrapped in TenantProvider) ── */
        {
          path: '/:slug',
          element: (
            <TenantProvider>
              <Outlet />
            </TenantProvider>
          ),
          children: [
            { index: true, element: <TenantSite /> },
            { path: 'quiz', element: <QuizPage /> },
            { path: 'book', element: <BookingPage /> },
            { path: 'shop', element: <RetailCheckout /> },
            { path: 'looks', element: <LooksPage /> },
            { path: 'flash', element: <FlashOpeningsPage /> },
            { path: 'portal', element: <ClientPortal /> },
            { path: 'login', element: <ClientLoginPage /> },
            { path: 'signup', element: <TenantSignupRedirect /> },
            { path: 'look-search', element: <VisualSearch /> },
          ],
        },

        /* ── Paragon Login (public) ── */
        { path: '/paragon/login', element: <ParagonLoginPage /> },

        /* ── PARAGON Agency Admin ── */
        {
          path: '/paragon/admin',
          element: (
            <RequireAuth>
              <RequireAgencyAdmin />
            </RequireAuth>
          ),
          children: [
            {
              element: <ParagonLayout />,
              children: [
                { index: true, element: <ParagonFleetHealth /> },
                { path: 'fleet', element: <ParagonFleetHealth /> },
                { path: 'migration', element: <ParagonMigrationDLQ /> },
                { path: 'migration/:jobId/dlq', element: <ParagonMigrationDLQ /> },
                { path: 'forge', element: <ParagonLibraryPanel /> },
                { path: 'impersonation', element: <ParagonImpersonation /> },
              ],
            },
          ],
        },

        /* ── Catch-all ── */
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
)

export function AppRouter() {
  return <RouterProvider router={router} />
}
